import { Grid, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FinancialFormField } from 'src/financial/financial-data/financial-item/forms/common/FinancialFormField';
import { GridPair } from 'src/financial/financial-data/financial-item/forms/common/GridPair';
import {
  OwnerSharesSection,
  productSupportsOwners,
} from 'src/financial/financial-data/financial-item/forms/common/OwnerSharesSection';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import { FormAccordionItem, ReadOnlyWrapper } from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import {
  valuationFieldsFor,
  VALUATION_SECTION,
} from 'src/financial/financial-data/financial-item/rework/financialItemRework';

/**
 * Said beside an item's value when an owner sits outside the household. The value itself counts
 * every share; it is the client's own totals that stop at theirs.
 */
const EXTERNAL_SHARE_NOTE =
  'The external share is part of this value, but not of the client’s totals';

type Props = {
  product: string;
  /** A feed owns the figure on a connected item, so the adviser reads it rather than edits it. */
  isUpdatedAutomatically?: boolean;
};

/**
 * The reworked Valuation section: who owns the item and in what share, then what it is worth
 * and when it was valued. Renders nothing while the financial rework is off, so each form can
 * place it unconditionally above its Summary and leave the old layout untouched behind the flag.
 */
export function ValuationAccordionItem({ product, isUpdatedAutomatically = false }: Props) {
  const { flagEnabledFinancialRework, flagEnabledFinancialItemOwners } = useFlags();
  const { uiData } = useDynamicUI();
  const [hasExternalOwners, setHasExternalOwners] = useState(false);

  const fields = valuationFieldsFor(product);
  if (!flagEnabledFinancialRework || !fields) {
    return null;
  }

  const metadata = uiData.ui.entitiesMetadata?.[product] ?? {};
  const has = (field?: string) => field != null && field in metadata;

  const showOwners = flagEnabledFinancialItemOwners && productSupportsOwners(product);
  const valueDate = has(fields.valueDateField) ? fields.valueDateField : undefined;

  const valuationFields = (
    <Grid>
      {valueDate && (
        <Grid.Col span={8}>
          <FinancialFormField label={fields.valueDateLabel} field={valueDate} product={product} />
        </Grid.Col>
      )}
      <Grid.Col span={valueDate ? 4 : 12}>
        <Stack gap={4}>
          <FinancialFormField
            label={fields.valueLabel}
            field={fields.valueField}
            product={product}
          />
          {/* The figure above is the item's whole value, which the owners table divides into
              shares — the external one among them. What leaves the external share out is the
              client's Financials, whose totals and net worth count only their own share. */}
          {hasExternalOwners && (
            <Text size="xs" c="gray.7">
              {EXTERNAL_SHARE_NOTE}
            </Text>
          )}
        </Stack>
      </Grid.Col>
    </Grid>
  );

  // The feed owns the figure and the date it was taken, so a connected item shows them the way
  // the Summary used to: read-only. Ownership is the adviser's either way, so it stays editable —
  // all but the money column of the owners table, which would otherwise write a new value back
  // through the share and save it over the feed's own.
  const valuation = isUpdatedAutomatically ? (
    <ReadOnlyWrapper>{valuationFields}</ReadOnlyWrapper>
  ) : (
    valuationFields
  );

  return (
    <FormAccordionItem value={VALUATION_SECTION} label="Valuation">
      {showOwners ? (
        <OwnerSharesSection
          product={product}
          valueField={fields.valueField}
          variant="valuation"
          isUpdatedAutomatically={isUpdatedAutomatically}
          onExternalOwnersChange={setHasExternalOwners}
        />
      ) : (
        has('is_joint') && (
          <Grid>
            <GridPair first={<FinancialFormField field="is_joint" product={product} />} />
          </Grid>
        )
      )}
      {has(fields.valueField) && valuation}
    </FormAccordionItem>
  );
}
